import "./styles/fonts.css";
import "./styles/vault.css";
import "./styles/app.css";

import { Api, ApiError } from "./api/client.js";
import type { Health, KycConfirmed, KycView, Preferences, SessionView } from "./api/types.js";
import { ActivityScreen } from "./app/activity.js";
import { Chat } from "./app/chat.js";
import { JudgeMode } from "./app/judge.js";
import { DemoFaultSwitch } from "./app/demo-fault.js";
import { PcmClip } from "./adapters/pcm-clip.js";
import { timingText, type RefreshProof } from "./app/judge-model.js";
import { MeScreen } from "./app/me.js";
import { PayeesScreen } from "./app/payees.js";
import { CardsScreen } from "./app/cards.js";
import { SecurityScreen } from "./app/security.js";
import { UNLOCK_WORDS } from "./app/security-model.js";
import { LimitsScreen } from "./app/limits.js";
import { KycScreen } from "./app/kyc-screen.js";
import { ScheduledScreen } from "./app/scheduled.js";
import { FixtureDrafter, type DraftFixture } from "./adapters/drafter.js";
import fixtureData from "./adapters/fixtures.generated.json" with { type: "json" };
import { homeModel, type HomeModel, type HomeReads } from "./app/home-model.js";
import { PERSONAS, type Persona } from "./app/personas.js";
import { doorFailure, offered } from "./app/door-model.js";
import { ReadAloudSetting } from "./app/read-aloud-setting.js";
import { ReadAloud } from "./app/read-aloud.js";
import { clock } from "./lib/format.js";
import { applyRoot, reducedMotion, setType } from "./lib/root.js";
import { installHintWanted, installShell, isStandalone } from "./lib/viewport.js";
import { installUpright } from "./lib/upright.js";
import { Nav } from "./lib/nav.js";
import { useSheetHistory } from "./ui/sheet.js";
import { accountDetail, type AccountDetail, type ActionId, type DetailReads } from "./app/account-model.js";
import { HomeView } from "./ui/home.js";
import { Door, type DoorBanner } from "./ui/signin.js";
import { leaveStream, returnToStream } from "./ui/stream-place.js";
import { InvestScreen } from "./app/invest.js";
import { tabBar, type Tab } from "./ui/tabs.js";
import type { AimHandlers } from "./ui/aim.js";
import { AimHints } from "./app/aim-hint.js";
import { WelcomeSeen, exampleFor, shouldOpen, stepsFor } from "./app/welcome-model.js";
import { ResumeStore, isPlace, type ChatKept, type Place, type Resume } from "./app/resume.js";
import { WelcomeLayer, type WelcomeEnd } from "./ui/welcome.js";
import { webSpeechFactory, withFallback, type RecognizerFactory } from "./adapters/voice.js";
import { webAudioOut, type AudioOut } from "./adapters/audio-out.js";
import { fakeSpeech, routeSpeech, speechFlag, type SpeechSource } from "./adapters/speech.js";
import { amendFlag, fakeAmend, routeAmend, type AmendSource } from "./adapters/amend.js";
import { asrDefault, asrFlag, fallsBackToBrowser, fetchAsrTicket, micCapture, selectRecognizerFactory, tencentAsrFactory } from "./adapters/tencent-asr.js";
import { installPress } from "./lib/press.js";
import { BankClock } from "./lib/bank-clock.js";
import { PageLeaving } from "./lib/page-leaving.js";
import { reloadOncePerSession, retireFromPage, sessionStorageOrUndefined } from "./lib/sw-retire.js";

/**
 * Boot and the three screens of run 1: the front door (09) → Home (01) ⇄ the
 * chat (02, with 08 and 03 as sheets). `#app` is the phone's own viewport; each
 * screen puts its regions (top bar · body · dock) straight into it, so the
 * system's `.phone .body` / `.phone .dock` rules apply unchanged.
 */
const root = document.documentElement;
const host = document.getElementById("app") as HTMLElement;
const env = { root, search: location.search, matchMedia: (q: string) => window.matchMedia(q) };
applyRoot(env);
// The shell never moves (§C.9): pinned to the visual viewport before the first screen.
installShell({
  doc: document,
  vv: window.visualViewport,
  innerHeight: () => window.innerHeight,
  innerWidth: () => window.innerWidth,
  matchMedia: (q) => window.matchMedia(q),
  navigator: navigator as Navigator & { standalone?: boolean },
  scroller: () => host.querySelector<HTMLElement>(":scope > .body"),
  raf: (fn) => void requestAnimationFrame(fn),
});

// Portrait only (Umakhan, 29 Sep): the manifest, a lock where the platform allows, and on iOS
// a bank surface over an inert app while the phone is on its side — the app's state untouched.
installUpright({
  doc: document,
  app: host,
  matchMedia: (q) => window.matchMedia(q),
  standalone: isStandalone((q) => window.matchMedia(q), navigator as Navigator & { standalone?: boolean }),
  orientation: lockable(screen.orientation),
});

/** `screen.orientation` when it has `lock()` (Chromium; not Safari), called on itself. */
function lockable(o: unknown): { lock(orientation: string): Promise<unknown> } | undefined {
  const so = o as { lock?: (orientation: string) => Promise<unknown> } | undefined;
  return typeof so?.lock === "function" ? { lock: (x) => (so.lock as (orientation: string) => Promise<unknown>).call(so, x) } : undefined;
}

// Press-in on every pressable (§C.6): [data-pressed] held ≥ 90 ms, plus iOS's :active.
installPress(document, { now: () => performance.now(), later: (fn, ms) => void setTimeout(fn, ms) });

// Back closes one layer (DCTA-84 A1): the chat, an account's detail and every sheet are history entries.
const nav = new Nav(window);
useSheetHistory(nav);

// Every time the app prints is the bank's (DCTA-85 P1-2): the bank's answers teach this clock.
const bank = new BankClock();
/** QA-04: a request a reload cancelled is not a failure (lib/page-leaving.ts) — asked when it fails, never held for good. */
const page = new PageLeaving(window);
const api = new Api((iso) => bank.learn(iso), { leaving: () => page.gone(), back: () => page.shown() });

// QA-04: this tab keeps the session across a reload (app/resume.ts) — in its own session storage, gone with the tab.
const resumeStore = new ResumeStore(() => window.sessionStorage);

/**
 * DCTA-141 × DCTA-142 × DCTA-162: the speech engine. Tencent's real-time ASR by default (the keys are live); the browser's
 * with `VITE_ASR=browser` for a build or `?asr=browser` for one page. Tencent's steps aside for the browser's when it is
 * "not here" (`fallsBackToBrowser`: 503, 429, Tencent's refusal or cap, a failed socket) — the same listening goes on,
 * so does every later one, and the chat says so once, quietly. The end-of-speech module is the same for both.
 */
/** DCTA-191 (ADR-0024): the listening's PCM, kept for the bank as the spoken turn's evidence — the engine fills it, the chat sends it. */
const clip = new PcmClip();
/** The page's microphone pipeline (DCTA-181) — DCTA-236 W1: its worklet loads after sign-in (`prepareVoice`), never in the first tap. */
const mic = micCapture();
const asrChoice = asrFlag(location.search, asrDefault(import.meta.env["VITE_ASR"]));
/** P1-9 (DCTA-192): Tencent's engine stepped aside for the browser's this session (judge mode's Speech row says so). */
let speechAside = false;
const recognizer: RecognizerFactory | undefined = selectRecognizerFactory(asrChoice, () =>
  withFallback(tencentAsrFactory({ ticket: () => fetchAsrTicket(api.csrf), clip, capture: mic }), webSpeechFactory(), fallsBackToBrowser, () => {
    speechAside = true;
    session?.chat?.engineAside();
  }),
);
const drafter = new FixtureDrafter(fixtureData as readonly DraftFixture[]);

/**
 * DCTA-236 W1 (prod-verify 15 Oct: beat 4, the first voice turn of a page, was heard from its second errand on): the
 * microphone is wired only once its worklet's module has loaded, so the module loads now — after sign-in, while Home is up —
 * instead of over the network inside the first tap. No microphone, nothing on screen.
 */
function prepareVoice(): void {
  // A short wait keeps the context's making off Home's first frame.
  if (asrChoice === "tencent") window.setTimeout(() => mic.prepare(), 250);
}

/**
 * DCTA-162 (spoken.md F30, F31): the bank's voice — DCTA-161's speech route (silent until it answers), or the stand-in
 * with `?speech=fake` (silent sentences: the playback, the Stop pill and barge-in, never words) — and the speaker, a
 * Web Audio context unlocked inside the customer's own tap.
 */
const speech: SpeechSource = speechFlag(location.search) === "fake" ? fakeSpeech() : routeSpeech({ csrf: () => api.csrf });
const speaker: AudioOut | undefined = webAudioOut();

/**
 * DCTA-170 (docs/design/v3/03-edit-in-place.md): a card's edits are made in place by the bank's amend route (B46, DCTA-171)
 * — the default. `?amend=off` puts Edit back on the conversation (as before B46); `?amend=fake` is the stand-in bank for the
 * design review, on this machine only — a card it makes cannot be signed, the gateway does not know it.
 */
const amendMode = amendFlag(location.search, location.hostname);
const amendMs = Number(new URLSearchParams(location.search).get("amend_ms") ?? "0");
const amend: AmendSource | undefined =
  amendMode === "fake"
    ? fakeAmend({ latencyMs: Number.isFinite(amendMs) ? Math.min(amendMs, 5000) : 0, accounts: () => api.accounts().then((r) => r.accounts) })
    : amendMode === "route"
      ? routeAmend(api)
      : undefined;

function mount(screen: string, parts: readonly HTMLElement[]): void {
  // DCTA-216: the walkthrough belongs to the screen it opened over; another screen ends it, not seen.
  if (welcome.isOpen) welcome.close("session");
  // QA-04: where a reload comes back to — the main screen last shown (a layer over one comes back as the one under it).
  if (session !== undefined && isPlace(screen)) session.place = screen;
  // DCTA-162 F31: leaving the chat — for any screen — stops the bank's voice.
  if (screen !== "chat") session?.chat?.silence();
  // P1-1 (DCTA-192): leaving the chat keeps its place, and what it already showed never rises again (ui/stream-place.ts).
  const stream = session?.chat?.view.body;
  if (stream !== undefined && host.dataset["screen"] === "chat" && screen !== "chat") leaveStream(stream);
  host.dataset["screen"] = screen;
  // The chat's glass dock pads the column by its measured height (ui/chat.ts); no other screen has it.
  host.style.removeProperty("--dock-pad");
  host.replaceChildren(...parts);
  if (stream !== undefined && screen === "chat") returnToStream(stream);
  delete host.dataset["enter"];
  // The first-visit hint belongs to Home's tab bar only.
  if (screen !== "home" && host.dataset["hint"] === "tip") delete host.dataset["hint"];
}

// ------------------------------------------------------------------ door
function door(banner: DoorBanner = null): void {
  setType(env, "normal");
  /** DCTA-185: "the demo is busy" is on screen — Retry signs in again (after "can't reach" it checks the bank). */
  let busyShown = false;
  const login = (persona: Persona, keepBanner: boolean): void => {
    view.busy(true);
    // A retry keeps the busy line until the bank answers (no flicker); Continue starts clean.
    if (!keepBanner) view.banner(null);
    // No profile from the door (QA-016): the bank picks it — the customer's own Large type
    // switch when set, else their record (ask k) — and the screen follows the session.
    api
      .login(persona.customerId)
      .then((session) => {
        // QA-04: this tab keeps the session — a reload comes straight back in while the bank still holds it.
        resumeStore.start(persona.customerId, api.csrf, session.demo_mode === true);
        signedIn(persona, session.display_name, session.accessibility_profile === "large_type", session.preferences, session.kyc, session.demo_mode === true);
      })
      .catch((error: unknown) => {
        view.busy(false);
        const said = doorFailure(error);
        busyShown = said !== null && typeof said === "object";
        view.banner(said);
      });
  };
  const view = new Door(
    {
      onPick: (p) => setType(env, p.profile === "large_type" ? "large" : "normal"),
      onRetry: () => {
        if (busyShown) {
          login(view.chosen, true);
          return;
        }
        view.busy(true);
        api
          .health()
          .then(() => view.banner(null))
          .catch(() => view.banner("error"))
          .finally(() => view.busy(false));
      },
      onContinue: (persona) => {
        busyShown = false;
        login(persona, false);
      },
    },
    undefined,
    { installHint: installHintWanted(navigator, isStandalone((q) => window.matchMedia(q), navigator as Navigator & { standalone?: boolean })) },
  );
  mount("signin", view.parts);
  view.banner(banner);
  // P0-1 (DCTA-192): the door offers only the customers the bank has (its personas read); if that read fails it keeps its
  // own list, and a login the bank refuses says so (doorFailure's "unavailable").
  api
    .personas()
    .then((answer) => view.offer(offered(PERSONAS, answer)))
    .catch(() => undefined);
  if (banner === null) {
    // The gateway unreachable is the one error the door knows about (§10.7).
    api.health().catch(() => view.banner("error"));
  }
}

// ------------------------------------------------------------------ home
interface Session {
  readonly persona: Persona;
  readonly name: string;
  /** DCTA-162 F29: the Read aloud choice, shared by Me (its sheet) and the chat (its policy). */
  readonly readAloud: ReadAloudSetting;
  readonly home: HomeView;
  readonly activity: ActivityScreen;
  readonly me: MeScreen;
  /** Round E batch 1 (DCTA-111): screens pushed under Me. */
  readonly security: SecurityScreen;
  readonly payees: PayeesScreen;
  /** DCTA-154: the Limits page — pushed under Me, or over the chat (a cap refusal's "See my limits"). */
  readonly limits: LimitsScreen;
  /** Over the chat: its own history entry, Back = the chat where it was. Under Me it is a pushed screen (pushEntry). */
  limitsEntry: number | undefined;
  limitsFrom: "me" | "chat";
  /** Judge mode (12): per session, off by default, only where the deployment runs demo mode. */
  judge: boolean;
  readonly judgeMode: JudgeMode;
  /** DCTA-188 Change 4: judge mode's one-shot faulty-drafter switch — judge mode draws it, the chat's next request reads it. */
  readonly demoFault: DemoFaultSwitch;
  /** DCTA-229: the Invest tab (spec 10-invest) — its reads, its range, its layers. */
  readonly invest: InvestScreen;
  last: HomeModel | undefined;
  /** The reads the last Home was built from — an account's detail starts from them. */
  reads: HomeReads | undefined;
  /** The history entries of the open chat and the open account detail. */
  chatEntry: number | undefined;
  detailEntry: number | undefined;
  /** The account whose detail is open (DCTA-131: read again after a signed payment). */
  detailKey: string | undefined;
  /** "Not on Home" (DCTA-85 P2-3): one entry while Activity, Invest or Me is showing — Back = Home. */
  tabEntry: number | undefined;
  /** A screen pushed under Me (Security, Payees) and a payee's page over Payees: one history entry each. */
  pushEntry: number | undefined;
  payeeEntry: number | undefined;
  /** DCTA-120: 17 at `add` — its history entry, where it came from, and the chat's scroll to come back to. */
  addEntry: number | undefined;
  addFrom: "list" | "chat";
  /**
   * A layer over the chat (add, Limits, the identity check) comes back to the EXACT place — "Back is the chat again, at the
   * same scroll position" — even when the chat's end moved meanwhile; ui/stream-place.ts alone would take a chat left at
   * its end to its new end (e2e scam-check's Limits-over-chat test: 20 px off without this). Applied after `mount`.
   */
  chatScroll: number | undefined;
  /** 15 Cards, over Security or over an account's detail (DCTA-111): its screen while open, its entry, where Back goes. */
  cards: CardsScreen | undefined;
  cardsEntry: number | undefined;
  cardsFrom: "security" | "detail";
  lastAt: string | undefined;
  chat: Chat | undefined;
  /** The tab the chat was opened from: its back button returns there. */
  from: Tab;
  /** DCTA-176 (B59): the identity check as the session shows it — the Home banner and Me's row read it. */
  kyc: KycView | undefined;
  /** The deployment runs demo mode (the refresh's routes exist only there). */
  readonly demo: boolean;
  /** DCTA-176 (F45/F46): the refresh while it is up, its history entry, and the screen it rose over. */
  kycScreen: KycScreen | undefined;
  kycEntry: number | undefined;
  kycFrom: "chat" | "home" | "me" | "invest";
  /** DCTA-176 (F49): the refresh that ran this session — judge mode's identity-check Proof. */
  kycProof: RefreshProof | undefined;
  /**
   * DCTA-209 (F58, F59): the Scheduled list and a schedule — one layer over Activity (its section, See all, a receipt's
   * schedule row) or over the chat (a signed schedule's View, a read card's row), with its history entry; a schedule pushed
   * over the list has one more. Back is where it came from, as it was.
   */
  scheduled: ScheduledScreen | undefined;
  scheduledEntry: number | undefined;
  scheduledDetailEntry: number | undefined;
  scheduledFrom: "activity" | "chat" | "invest";
  /** DCTA-216: the first Home after this sign-in has not been shown yet — the walkthrough may open on it. */
  welcomeDue: boolean;
  /** QA-04: the main screen last shown — where a reload comes back to. */
  place: Place;
  /** QA-04: the conversation a reload kept, drawn back into the chat when it is first made. */
  kept: ChatKept | undefined;
}

let session: Session | undefined;

/**
 * The assistant's mark, the same on every tab (§C.3): tap = the chat, text first; hold
 * or slide up = the chat, listening. The microphone is off without an engine, after a
 * refusal, or offline — then nothing charges and a hold says so.
 */
const hints = new AimHints(host, document, () => window.localStorage, (fn, ms) => void setTimeout(fn, ms));
const assistant: AimHandlers = {
  onAssistant: (entry) => openChat(entry, welcomeFound()),
  voiceOff: () => !navigator.onLine || (session?.chat === undefined ? recognizer === undefined : !session.chat.voiceAvailable),
  onVoiceOff: () => hints.voiceOff(),
};

/**
 * DCTA-216 (docs/design/v3/10-welcome.md §1): the first-run welcome. By itself once per device and customer, 400 ms after
 * the first Home of a sign-in, when judge mode is off and nothing (a chat, a sheet, a passkey or platform prompt, a
 * signing) is open; again on demand from Me › Help › How it works, in either mode. Seen on Done, Skip, Esc, the system
 * Back or a use of the lit mark — and when the mark is used before it opens (they found the way in). A session that ends
 * under it leaves it unseen. Never spoken.
 */
const WELCOME_AFTER_MS = 400;
const welcomeSeen = new WelcomeSeen(() => window.localStorage);
const welcome = new WelcomeLayer({ host, reduced: () => reducedMotion(root), history: nav, onClose: (end) => welcomeClosed(end) });
let welcomeTimer: ReturnType<typeof setTimeout> | undefined;

function welcomeClosed(end: WelcomeEnd): void {
  const s = session;
  if (end === "session" || s === undefined) return;
  welcomeSeen.mark(s.persona.customerId);
  hints.seen();
}

/**
 * The lit mark was used: an open walkthrough closes (seen) and hands its history entry to the chat the mark opens (the
 * return value); one about to open never does (seen as well).
 */
function welcomeFound(): number | undefined {
  if (welcome.isOpen) return welcome.handOver();
  if (welcomeTimer === undefined) return undefined;
  clearTimeout(welcomeTimer);
  welcomeTimer = undefined;
  if (session !== undefined) welcomeSeen.mark(session.persona.customerId);
  hints.seen();
  return undefined;
}

function welcomeOff(): void {
  if (welcomeTimer !== undefined) clearTimeout(welcomeTimer);
  welcomeTimer = undefined;
  welcome.close("session");
}

function armWelcome(s: Session): void {
  if (welcomeTimer !== undefined) clearTimeout(welcomeTimer);
  welcomeTimer = setTimeout(() => {
    welcomeTimer = undefined;
    if (session !== s) return;
    const open = shouldOpen({
      seen: welcomeSeen.has(s.persona.customerId),
      onHome: host.dataset["screen"] === "home" && s.detailEntry === undefined,
      judge: s.judge,
      busy: nav.open > 0 || host.querySelector("dialog[open]") !== null,
    });
    if (open) openWelcome(s, "home");
  }, WELCOME_AFTER_MS);
}

function openWelcome(s: Session, over: "home" | "me"): void {
  const id = s.persona.customerId;
  const screen = over === "home" ? { parts: s.home.parts, mark: s.home.mark } : { parts: s.me.view.parts, mark: s.me.view.mark };
  welcome.open({
    steps: stepsFor(id, s.kyc),
    example: exampleFor(id, s.kyc),
    voiceOff: assistant.voiceOff?.() === true,
    screen,
    returnTo: over === "home" ? s.home.mark : s.me.view.howRow,
  });
}

/** QA-04: a reload brought the session back — where it was, judge mode, and what the chat showed. */
interface Resumed {
  readonly place: Place;
  readonly judge: boolean;
  readonly chat: ChatKept | undefined;
}

function signedIn(persona: Persona, name: string, largeType: boolean, preferences?: Preferences, kyc?: KycView, demo = false, resumed?: Resumed): void {
  setType(env, largeType ? "large" : "normal");
  // DCTA-162 F29: the Read aloud choice — the bank's switch until B43, this device's note beside it.
  const readAloud = new ReadAloudSetting(persona.customerId, () => window.localStorage);
  readAloud.load(preferences);
  // DCTA-188: off by default; one message, then off by itself; off again with judge mode.
  const demoFault = new DemoFaultSwitch();
  const home = new HomeView({
    assistant,
    onRetry: () => void refreshHome(),
    // DCTA-231 V2: the lock banner's Unlock — Me › Security, as Invest's.
    onUnlock: () => {
      go("me");
      openPushed("security");
    },
    onOpen: (key) => openDetail(key),
    detail: {
      onBack: () => closeDetail(),
      onAction: (id, d) => detailAction(id, d),
      onSeeAll: (account) => {
        const handed = closeDetail(true);
        const now = session;
        if (now === undefined) return;
        tabLayer(now, "activity", handed);
        now.from = "activity";
        mount("activity", now.activity.view.parts);
        void now.activity.showAccount(account);
      },
    },
    onTab: go,
  });
  const activity = new ActivityScreen({
    api,
    host,
    now: () => bank.msOr(),
    reduced: () => reducedMotion(root),
    judgeOn: () => session?.judge === true,
    onProof: (bundleId) => void session?.judgeMode.proof(bundleId, showActivity, "Back to Activity"),
    onTab: go,
    assistant,
    onSignedOut: signedOut,
    // DCTA-209 (F57): a Scheduled row, a "Set up" row, a receipt's schedule row — the schedule; "See all" — the list.
    onSchedule: (mandateId) => openScheduled("activity", mandateId),
    onSeeAll: () => openScheduled("activity"),
  });
  const me = new MeScreen({
    api,
    name,
    initials: persona.initials,
    judge: () => session?.judge === true,
    onJudge: (on) => {
      if (session === undefined) return;
      session.judge = on;
      session.me.view.judge(on);
      // DCTA-188: the faulty-drafter switch belongs to judge mode — never left on outside it.
      if (!on) session.demoFault.set(false);
      judgeSurface();
    },
    onLargeType: (on) => setType(env, on ? "large" : "normal"),
    onSignedOut: (why) => (why === "chosen" ? leave() : signedOut()),
    onOpen: (where) => openPushed(where),
    onTab: go,
    assistant,
    readAloud,
    host,
    reduced: () => reducedMotion(root),
    onKyc: () => void openKyc("me"),
    // DCTA-216 (Claude's ruling 1, 14 Oct): in judge mode too — a judge may want to replay it.
    onHowItWorks: () => {
      if (session !== undefined && !welcome.isOpen) openWelcome(session, "me");
    },
  });
  const security = new SecurityScreen({
    api,
    host,
    reduced: () => reducedMotion(root),
    onBack: () => backToMe(),
    onFreeze: () => openCards("security"),
    onSignedOut: signedOut,
    onLocked: bankChanged,
    // DCTA-232 F1: the way out of the lock — the bank builds the unlock card from the customer's words.
    onUnlock: () => askInChat(UNLOCK_WORDS),
    onTab: go,
    assistant,
  });
  const payees = new PayeesScreen({
    api,
    now: () => bank.msOr(),
    onBack: () => backToMe(),
    onDetail: (open) => payeePage(open),
    onAdd: () => openAdd("list"),
    onAddBack: () => closeAdd(),
    host,
    reduced: () => reducedMotion(root),
    // DCTA-124: an add is signed with this customer's passkey (Check and sign, §F.2 13e).
    customerId: persona.customerId,
    onSignedOut: signedOut,
    onTab: go,
    assistant,
    onCard: readCard,
  });
  const limits = new LimitsScreen({
    api,
    now: () => bank.msOr(),
    onBack: () => (session?.limitsFrom === "chat" ? closeLimits() : backToMe()),
    onSignedOut: signedOut,
    onTab: go,
    assistant,
  });
  const judgeMode = new JudgeMode({
    api,
    drafter,
    mount: (parts) => mount("judge", parts),
    dock: () => tabBar("me", { onTab: go, ...assistant }, { pushed: true }).dock,
    timing: () => timingText(session?.chat?.timings.draft, session?.chat?.timings.confirm),
    onSignedOut: signedOut,
    demoFault,
    // DCTA-236 W2: a run of due payments moved money — Home (and Invest, once drawn) read again at once.
    onBankChanged: bankChanged,
    // DCTA-176 (F49): the turn the identity check refused (kept by the chat) and the refresh that ran (kept by the flow).
    identity: () => {
      const turn = session?.chat?.eligibilityRefusal;
      return {
        turn: turn === undefined ? undefined : { said: turn.said, outcome: turn.outcome, at: turn.at, customerId: persona.customerId },
        refresh: session?.kycProof,
      };
    },
    // P1-9 (DCTA-192): Models and timing's Speech row, from what this session actually runs.
    speech: () => ({
      input: asrChoice,
      fellBack: speechAside,
      output: speechFlag(location.search) === "fake" ? "fake" : speaker === undefined ? "none" : "tencent",
      readAloud: readAloud.mode,
    }),
  });
  // DCTA-229 (spec 10-invest §6.4): Unlock is Me › Security; Update now the identity check's refresh (only where it exists:
  // demo mode, Home's rule); a top-up its schedule over the tab; See all (Orders) is Activity; a receipt's Verified the proof.
  const invest = new InvestScreen({
    api,
    host,
    now: () => bank.msOr(),
    reduced: () => reducedMotion(root),
    judgeOn: () => session?.judge === true,
    onProof: (bundleId) => void session?.judgeMode.proof(bundleId, showInvest, "Back to Invest"),
    onTab: go,
    assistant,
    onSignedOut: signedOut,
    lapsed: () => session !== undefined && kycLapsed(session),
    onUpdateKyc: () => void openKyc("invest"),
    onUnlock: () => {
      go("me");
      openPushed("security");
    },
    onSchedule: (mandateId) => openScheduled("invest", mandateId),
    onSeeAllOrders: () => go("activity"),
  });
  prepareVoice();
  session = { persona, name, readAloud, home, activity, me, security, payees, limits, limitsEntry: undefined, limitsFrom: "me", invest, judge: false, judgeMode, demoFault, last: undefined, reads: undefined, lastAt: undefined, chat: undefined, from: "home", chatEntry: undefined, detailEntry: undefined, detailKey: undefined, tabEntry: undefined, pushEntry: undefined, payeeEntry: undefined, addEntry: undefined, addFrom: "list", chatScroll: undefined, cards: undefined, cardsEntry: undefined, cardsFrom: "detail", kyc, demo, kycScreen: undefined, kycEntry: undefined, kycFrom: "home", kycProof: undefined, scheduled: undefined, scheduledEntry: undefined, scheduledDetailEntry: undefined, scheduledFrom: "activity", welcomeDue: resumed === undefined || resumed.place === "home", place: "home", kept: resumed?.chat };
  if (resumed === undefined) {
    showHome();
    return;
  }
  // QA-04: judge mode is the session's — a reload keeps it, as it keeps the session.
  if (resumed.judge && demo) {
    session.judge = true;
    me.view.judge(true);
  }
  if (resumed.place === "home") showHome();
  else if (resumed.place === "chat") resumeChat(session);
  else resumeTab(session, resumed.place);
}

/** QA-04: back on a tab after a reload — on the entry the browser is on (Back = Home, as before the reload). */
function resumeTab(s: Session, tab: Exclude<Tab, "home">): void {
  s.tabEntry = nav.adopt(tabPop(s));
  show(tab);
}

/** The tab bar's one way around: Home, Activity, Invest, Me. */
function go(tab: Tab): void {
  // A tab from an account's detail leaves the detail (the Home tab = Home's root); the detail's
  // history entry becomes the tab's, so Back from that tab is Home (P2-3). A screen pushed under
  // Me does the same (DCTA-111). The add screen goes with its layer — and the chat under it, when
  // it came from the chat (DCTA-120).
  const addHanded = leaveAdd() ?? leaveLimits();
  closeCards();
  let handed = closeDetail(tab !== "home") ?? closePushed(tab !== "home");
  // DCTA-209: the Scheduled layer goes with the tab; its entry is handed over as a closing layer's is.
  const schedHanded = closeScheduled(tab !== "home", false);
  if (schedHanded !== undefined) {
    if (handed !== undefined) nav.done(handed);
    handed = schedHanded;
  }
  // The add screen's entry is the newest: it is the one handed over (Nav.swap), and the screen's entry
  // under it goes without a back() of its own (a back() racing the tab's push would close the tab).
  if (addHanded !== undefined) {
    if (handed !== undefined) nav.done(handed);
    handed = addHanded;
  }
  if (session !== undefined) tabLayer(session, tab, handed);
  show(tab);
}

/**
 * Back from Activity / Invest / Me → Home; Back on Home leaves (ruling P2-3, Android's convention).
 * One history entry stands for "not on Home": a tab-to-tab switch keeps it, the Home tab consumes
 * it, Back runs it. A layer closing as a tab opens (the detail, the chat) hands over its entry.
 */
function tabLayer(s: Session, tab: Tab, handover?: number): void {
  if (tab === "home" || s.tabEntry !== undefined) {
    if (handover !== undefined) nav.done(handover);
    if (tab === "home" && s.tabEntry !== undefined) nav.done(s.tabEntry);
    if (tab === "home") s.tabEntry = undefined;
    return;
  }
  const pop = tabPop(s);
  s.tabEntry = handover === undefined ? nav.push(pop) : nav.swap(handover, pop);
}

/** Back from a tab that is not Home: Home. */
function tabPop(s: Session): () => boolean {
  return () => {
    s.tabEntry = undefined;
    showHome();
    return true;
  };
}

/** Back to a tab as it was (from the chat): an account's detail open under Home stays open. */
function show(tab: Tab): void {
  if (tab === "home") showHome();
  else if (tab === "activity") showActivity();
  else if (tab === "invest") showInvest();
  else showMe();
}

/** Invest (DCTA-229, spec 10-invest): the tab's own reads — its last ones first, so nothing blinks, then fresh ones. */
function showInvest(): void {
  const s = session;
  if (s === undefined) return;
  s.from = "invest";
  mount("invest", s.invest.view.parts);
  s.invest.show();
}

function showMe(): void {
  const s = session;
  if (s === undefined) return;
  s.from = "me";
  mount("me", s.me.view.parts);
  void s.me.refresh().then(judgeSurface);
}

/** 12 on: the judge surface under Me's list — and, as it appears, the switch and the surface come into view together. */
function judgeSurface(): void {
  const s = session;
  if (s === undefined) return;
  const slot = s.me.view.judgeSlot;
  if (!s.judge) {
    slot.replaceChildren();
    return;
  }
  void s.judgeMode.home(slot, showMe).then(() => {
    const body = s.me.view.body;
    const row = body.querySelector<HTMLElement>(".judge-switch");
    if (row !== null) body.scrollTop = Math.max(0, row.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 8);
  });
}

function showActivity(): void {
  const s = session;
  if (s === undefined) return;
  s.from = "activity";
  mount("activity", s.activity.view.parts);
  void s.activity.refresh();
}

/**
 * DCTA-176 (§F.2 26a, F48): Home's banner follows the session's identity check — while it is due, and only where the
 * refresh exists (demo mode), "Update now" opens it over Home. P1-5 (DCTA-192): a lapsed check (the bank's `restricted`)
 * gets its own words on the same banner — payments are stopped until it is updated.
 */
/** The identity check has lapsed, where its refresh exists (demo mode): Home's banner and Invest's say so (P1-5, DCTA-229). */
function kycLapsed(s: Session): boolean {
  return s.demo && s.kyc?.status === "restricted";
}

function homeDue(s: Session): void {
  const update = (): void => void openKyc("home");
  if (kycLapsed(s)) {
    s.home.lapsed(update);
    return;
  }
  const due = s.demo && s.kyc?.status === "refresh_due" ? s.kyc.update_by : undefined;
  s.home.due(due, update);
}

function showHome(): void {
  if (session === undefined) return;
  session.from = "home";
  homeDue(session);
  mount("home", session.home.parts);
  // DCTA-216: the first Home of a sign-in may bring the walkthrough (it replaces the one-time tip); every other Home the tip.
  const due = session.welcomeDue;
  session.welcomeDue = false;
  if (due && !welcomeSeen.has(session.persona.customerId)) armWelcome(session);
  else hints.firstVisit();
  if (session.last === undefined) session.home.loading(session.name, session.persona.initials);
  else session.home.show(session.last);
  void refreshHome();
}

async function refreshHome(): Promise<void> {
  const s = session;
  if (s === undefined) return;
  try {
    const reads = await homeReads(s);
    const model = homeModel(reads, bank.msOr());
    s.reads = reads;
    s.last = model;
    s.lastAt = reads.asOf ?? bank.iso();
    if (host.dataset["screen"] === "home") s.home.show(model);
  } catch (error) {
    if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
      signedOut();
      return;
    }
    if (s.last !== undefined && s.lastAt !== undefined) s.home.stale(clock(s.lastAt));
    else s.home.unreachable();
  }
}

async function homeReads(s: Session): Promise<HomeReads> {
  const [accounts, cards, portfolio, ledger, valuation] = await Promise.all([
    api.accounts(),
    api.cards(),
    api.portfolio(),
    api.ledgerEntries(3),
    // P2-11: the bank's own total; an older gateway without the read leaves the header bare.
    api.valuation().catch(() => undefined),
  ]);
  const holdings = await Promise.all(
    portfolio.holdings.map(async (h) => {
      const [named, quote] = await Promise.all([
        api.instruments(h.instrument_id).catch(() => ({ instruments: [] })),
        api.quote(h.instrument_id).catch(() => undefined),
      ]);
      const ref = named.instruments.find((i) => i.instrument_id === h.instrument_id);
      return {
        ...h,
        name: ref?.name ?? h.instrument_id,
        ticker: ref?.ticker ?? "",
        price: quote?.quote.currency === h.currency ? quote.quote.price : undefined,
      };
    }),
  );
  // P1-2: a reservation on Recent says when it goes out — its bundle's receipt knows.
  const holds = new Map<string, string | null>();
  const reserved = [...new Set(ledger.entries.filter((e) => e.entry_type === "reservation" && e.bundle_id !== undefined).map((e) => e.bundle_id as string))];
  await Promise.all(
    reserved.map(async (id) => {
      const receipt = await api.receipt(id).catch(() => undefined);
      const until = receipt?.steps.map((r) => (r.state === "held" ? r.hold_until : undefined)).find((u) => u !== undefined);
      if (until !== undefined) holds.set(id, until);
      else if (receipt !== undefined) holds.set(id, null);
    }),
  );
  return {
    name: s.name,
    initials: s.persona.initials,
    accounts: accounts.accounts,
    cards: cards.cards,
    holdings,
    recent: ledger.entries,
    holds,
    valuation,
    asOf: accounts.as_of,
  };
}

// ------------------------------------------------------------------ an account's detail (§C.5)
function openDetail(key: string): void {
  const s = session;
  const reads = s?.reads;
  if (s === undefined || reads === undefined) return;
  const first = accountDetail(detailBase(reads, key));
  if (first === undefined) return;
  const needsLines = first.kind !== "invest";
  s.detailKey = key;
  s.home.push(first, needsLines);
  if (s.detailEntry !== undefined) nav.done(s.detailEntry);
  s.detailEntry = nav.push(() => {
    s.detailEntry = undefined;
    s.detailKey = undefined;
    s.home.pop();
    return true;
  });
  if (!needsLines) return;
  void detailLines(s, key).then(
    (d) => {
      if (d !== undefined && s.home.detailOpen && s.detailKey === key) s.home.showDetail(d);
    },
    (error: unknown) => {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") signedOut();
      // The hero stays (Home's own figures); the rows cannot be read — say so, claim nothing.
      else if (s.home.detailOpen && s.detailKey === key) s.home.showDetail({ ...first, stats: [], rows: [], nothing: "Can't reach the bank right now. Nothing has changed." });
    },
  );
}

/** What a detail is built from: the reads Home was last built from, for one account (no lines yet). */
function detailBase(reads: HomeReads, key: string): DetailReads {
  return { key, asOf: reads.asOf ?? new Date(bank.msOr()).toISOString(), accounts: reads.accounts, cards: reads.cards, valuation: reads.valuation, lines: [] };
}

/** The account's own lines (a card's from the ledger), then its detail drawn with them and with Home's latest reads. */
async function detailLines(s: Session, key: string): Promise<AccountDetail | undefined> {
  const LIMIT = 200;
  const credit = s.reads?.cards.some((c) => c.card_id === key && c.type === "credit") === true;
  const { lines, full } = credit
    ? await api.ledgerEntries(LIMIT).then((r) => ({ lines: r.entries.filter((e) => e.account_id === `card_${key}`), full: r.entries.length < LIMIT }))
    : await api.statement(key, LIMIT).then((r) => ({ lines: r.entries.map((e) => ({ ...e, account_id: key })), full: r.entries.length < LIMIT }));
  const reads = s.reads;
  return reads === undefined ? undefined : accountDetail({ ...detailBase(reads, key), lines, truncated: !full });
}

/**
 * DCTA-131 (iPhone #3: "S$613.40 owed" beside the bank's "Nothing is owed"): a signed payment ran. Home is read again,
 * and so is the account's page the chat was opened from ("Pay the bill" on the card's page) — its hero, tiles and rows,
 * drawn once the fresh lines are in, so the page never blinks to empty. Whatever is not on screen is simply ready.
 */
/**
 * DCTA-231 V2: the bank changed under this session without a signature (a lock, a lower limit, a frozen card, a stopped
 * schedule) — the session goes on, so Home (and an account's page under it) and Invest, once drawn, read again now: the
 * customer who locked never meets the unlocked state again.
 */
function bankChanged(): void {
  void afterPayment();
  const s = session;
  if (s?.invest.drawn === true) void s.invest.refresh();
}

async function afterPayment(): Promise<void> {
  const s = session;
  if (s === undefined) return;
  await refreshHome();
  const key = s.detailKey;
  if (key === undefined || !s.home.detailOpen) return;
  const reads = s.reads;
  const hero = reads === undefined ? undefined : accountDetail(detailBase(reads, key));
  if (hero === undefined) return;
  if (hero.kind === "invest") {
    s.home.showDetail(hero);
    return;
  }
  const d = await detailLines(s, key).catch(() => undefined);
  if (d !== undefined && s.home.detailOpen && s.detailKey === key) s.home.showDetail(d);
}

/**
 * The detail's own back, or a tab: Home again, and its history entry consumed — or, with
 * `handOver`, returned unconsumed for the layer that replaces it (Nav.swap, P2-3).
 */
function closeDetail(handOver = false): number | undefined {
  const s = session;
  if (s === undefined || !s.home.detailOpen) return undefined;
  const id = s.detailEntry;
  s.detailEntry = undefined;
  s.detailKey = undefined;
  if (id !== undefined && !handOver) nav.done(id);
  s.home.pop();
  return handOver ? id : undefined;
}

/**
 * Every action opens the assistant with this account chosen (§C.5) — one way to move money:
 * pay / top up / invest / add cash name the account in the field; the card's bill, its
 * details and its statement are asked for in words, as the customer would.
 */
function detailAction(id: ActionId, d: AccountDetail): void {
  // Round E batch 1 (§E.4, DCTA-111): an account's "Card details" is 15 Cards, that account's card shown.
  if (id === "card") {
    openCards("detail", d.key, d.title);
    return;
  }
  const words: Partial<Record<ActionId, string>> = { card: "Show my card details", bill: "Pay my credit card bill", statement: "How much is my card bill?" };
  const context: Partial<Record<ActionId, string>> = { pay: ` from ${d.context}`, topup: ` into ${d.context}`, invest: ` from ${d.context}`, addcash: ` into ${d.context}` };
  openChat("text");
  const chat = session?.chat;
  if (chat === undefined) return;
  const said = words[id];
  if (said !== undefined) void chat.say(said);
  else chat.prefill(context[id] ?? "");
}

// ------------------------------------------------------------------ pushed under Me (DCTA-111, round E batch 1)
/** Me → 19 Security, 17 Payees or Limits: a screen over Me with one history entry; Back (its own, or the system's) is Me. */
function openPushed(where: "security" | "payees" | "limits"): void {
  const s = session;
  if (s === undefined) return;
  s.from = "me";
  if (where === "limits") {
    s.limitsFrom = "me";
    s.limits.view.backTo("Back to Me");
  }
  const screen = where === "security" ? s.security : where === "payees" ? s.payees : s.limits;
  mount(where, screen.view.parts);
  if (s.pushEntry === undefined) {
    s.pushEntry = nav.push(() => {
      s.pushEntry = undefined;
      showMe();
      return true;
    });
  }
  void screen.refresh();
}

/**
 * 15 Cards over Security ("Freeze a card") or over an account's detail ("Card details"): one more layer
 * with its own history entry; Back is where it came from, as it was.
 */
function openCards(from: "security" | "detail", selected?: string, fromTitle?: string): void {
  const s = session;
  if (s === undefined) return;
  closeCards();
  const screen = new CardsScreen({
    api,
    host,
    reduced: () => reducedMotion(root),
    tab: from === "security" ? "me" : "home",
    backLabel: from === "security" ? "Back to Security" : `Back to ${fromTitle ?? "the account"}`,
    onBack: () => {
      closeCards();
      backFromCards(from);
    },
    onPay: () => {
      closeCards();
      askInChat("Pay my credit card bill");
    },
    onSignedOut: signedOut,
    onTab: go,
    assistant,
  });
  s.cards = screen;
  s.cardsFrom = from;
  mount("cards", screen.view.parts);
  s.cardsEntry = nav.push(() => {
    s.cardsEntry = undefined;
    s.cards?.dispose();
    s.cards = undefined;
    backFromCards(from);
    return true;
  });
  void screen.refresh(selected);
}

function backFromCards(from: "security" | "detail"): void {
  const s = session;
  if (s === undefined) return;
  if (from === "security") {
    mount("security", s.security.view.parts);
    void s.security.refresh();
  } else showHome();
}

/** The Cards layer closes: its details masked, its entry consumed — or handed to the layer that replaces it. */
function closeCards(handOver = false): number | undefined {
  const s = session;
  if (s === undefined) return undefined;
  s.cards?.dispose();
  s.cards = undefined;
  const id = s.cardsEntry;
  s.cardsEntry = undefined;
  if (id === undefined || handOver) return id;
  nav.done(id);
  return undefined;
}

/** The pushed screen's own back. */
function backToMe(): void {
  closePushed();
  showMe();
}

/**
 * The pushed screen (and a payee's page over it) closes: their entries go — or, with `handOver`,
 * the screen's entry is returned unconsumed for the layer that replaces it (Nav.swap, P2-3).
 */
function closePushed(handOver = false): number | undefined {
  const s = session;
  if (s === undefined) return undefined;
  if (s.payeeEntry !== undefined) {
    nav.done(s.payeeEntry);
    s.payeeEntry = undefined;
    s.payees.closeDetail();
  }
  const id = s.pushEntry;
  s.pushEntry = undefined;
  if (id === undefined) return undefined;
  if (handOver) return id;
  nav.done(id);
  return undefined;
}

/**
 * DCTA-120 — 17 at `add`, one more layer with its own history entry: over the Payees list (its +), or
 * over the chat (the assistant's pointer, spec §F.2 5c) — shown at once, and Back (its own, or the
 * system's) is the chat again, at the same scroll position.
 */
function openAdd(from: "list" | "chat"): void {
  const s = session;
  if (s === undefined || s.addEntry !== undefined) return;
  s.addFrom = from;
  if (from === "chat") {
    s.chatScroll = s.chat?.view.body.scrollTop;
    mount("payees", s.payees.view.parts);
  }
  s.payees.openAdd(from);
  s.addEntry = nav.push(() => {
    // DCTA-124: Check and sign is part of this layer — the system's Back takes it back to the name (and
    // is refused while a passkey sheet is up); the layer and its entry stay.
    if (s.payees.stepBack()) return false;
    s.addEntry = undefined;
    afterAdd(s);
    return true;
  });
}

/** The add screen's own back: its entry consumed, and back where it came from. */
function closeAdd(): void {
  const s = session;
  if (s === undefined || s.addEntry === undefined) return;
  nav.done(s.addEntry);
  s.addEntry = undefined;
  afterAdd(s);
}

function afterAdd(s: Session): void {
  s.payees.closeAdd();
  if (s.addFrom !== "chat" || s.chat === undefined) return;
  mount("chat", s.chat.view.parts);
  if (s.chatScroll !== undefined) s.chat.view.body.scrollTop = s.chatScroll;
}

/**
 * Another layer takes over from the add screen (a tab, the mark): the screen closes and its history
 * entry — the newest — is RETURNED, for the next layer to take over (Nav.swap; a done() here would
 * back() asynchronously and close whatever is pushed next). The chat under it, when it came from
 * there, closes without a Back of its own (an older entry: no back()).
 */
function leaveAdd(): number | undefined {
  const s = session;
  if (s === undefined || s.addEntry === undefined) return undefined;
  const id = s.addEntry;
  s.addEntry = undefined;
  s.payees.closeAdd();
  if (s.addFrom === "chat" && s.chatEntry !== undefined) {
    nav.done(s.chatEntry);
    s.chatEntry = undefined;
  }
  return id;
}

/** A payee's page is one more layer over Payees: the system's Back closes the page, not Payees. */
function payeePage(open: boolean): void {
  const s = session;
  if (s === undefined) return;
  if (open) {
    if (s.payeeEntry === undefined) {
      s.payeeEntry = nav.push(() => {
        s.payeeEntry = undefined;
        s.payees.closeDetail();
        return true;
      });
    }
    return;
  }
  if (s.payeeEntry !== undefined) nav.done(s.payeeEntry);
  s.payeeEntry = undefined;
}

// ------------------------------------------------------------------ Scheduled (DCTA-209, F58/F59)
/**
 * The Scheduled list (`mandateId` undefined: "See all") or a schedule, one layer over Activity or over the chat with its
 * own history entry. A schedule opened from a row of the list is pushed over it with one more entry (Back = the list).
 * Opened while the layer is up (a second View), the schedule is shown in it. Back (its own, or the system's) is where it
 * came from, as it was — the chat at the same scroll position.
 */
function openScheduled(from: "activity" | "chat" | "invest", mandateId?: string): void {
  const s = session;
  if (s === undefined) return;
  const backLabel = (where: "activity" | "chat" | "invest"): string => (where === "chat" ? "Back to Assistant" : where === "invest" ? "Back to Invest" : "Back to Activity");
  if (s.scheduled !== undefined && s.scheduledEntry !== undefined) {
    if (mandateId !== undefined) void s.scheduled.openDetail(mandateId, backLabel(s.scheduledFrom));
    return;
  }
  if (from === "chat") s.chatScroll = s.chat?.view.body.scrollTop;
  s.scheduledFrom = from;
  const screen = new ScheduledScreen({
    api,
    host,
    reduced: () => reducedMotion(root),
    onBack: () => closeScheduled(),
    onDetail: (open) => {
      if (open) {
        if (s.scheduledDetailEntry === undefined) {
          s.scheduledDetailEntry = nav.push(() => {
            s.scheduledDetailEntry = undefined;
            s.scheduled?.popDetail();
            return true;
          });
        }
        return;
      }
      if (s.scheduledDetailEntry !== undefined) nav.done(s.scheduledDetailEntry);
      s.scheduledDetailEntry = undefined;
    },
    // P1 (row 28i): a change is a new card — the chat, its composer focused.
    onChange: () => {
      if (s.scheduledFrom === "chat") {
        closeScheduled();
        s.chat?.open("text");
      } else openChat("text");
    },
    onSignedOut: signedOut,
  });
  s.scheduled = screen;
  mount("scheduled", screen.view.parts);
  s.scheduledEntry = nav.push(() => {
    s.scheduledEntry = undefined;
    if (s.scheduledDetailEntry !== undefined) {
      nav.done(s.scheduledDetailEntry);
      s.scheduledDetailEntry = undefined;
    }
    afterScheduled(s);
    return true;
  });
  if (mandateId !== undefined) void screen.openDetail(mandateId, backLabel(from));
  else void screen.openList();
}

/**
 * The layer closes: its entries consumed — or, with `handOver`, the layer's entry returned unconsumed for the layer that
 * replaces it (Nav.swap, P2-3). `back`: go back where it came from (a tab taking over goes elsewhere).
 */
function closeScheduled(handOver = false, back = true): number | undefined {
  const s = session;
  if (s === undefined || s.scheduled === undefined) return undefined;
  if (s.scheduledDetailEntry !== undefined) nav.done(s.scheduledDetailEntry);
  s.scheduledDetailEntry = undefined;
  const id = s.scheduledEntry;
  s.scheduledEntry = undefined;
  if (back) afterScheduled(s);
  else {
    s.scheduled.dispose();
    s.scheduled = undefined;
  }
  if (id === undefined || handOver) return id;
  nav.done(id);
  return undefined;
}

function afterScheduled(s: Session): void {
  s.scheduled?.dispose();
  s.scheduled = undefined;
  // Focus goes back to what opened the layer (WCAG 2.4.3): the chat's View or read row; Activity's row or See all, once drawn.
  if (s.scheduledFrom === "chat" && s.chat !== undefined) {
    mount("chat", s.chat.view.parts);
    if (s.chatScroll !== undefined) s.chat.view.body.scrollTop = s.chatScroll;
    s.chat.returnFocus();
    return;
  }
  // DCTA-229: a top-up opened from the Invest tab's Coming up goes back there, focus on its row.
  if (s.scheduledFrom === "invest") {
    showInvest();
    s.invest.returnFocus();
    return;
  }
  showActivity();
  s.activity.returnFocus();
}

// ------------------------------------------------------------------ the Limits page over the chat (DCTA-154)
/**
 * A cap refusal's "See my limits" (backend DCTA-153 ruling 3): the Limits page over the chat, one more layer with its own
 * history entry — shown at once, and Back (its own, or the system's) is the chat again, at the same scroll position, the
 * chip still there to open it again. Nothing from the conversation travels to the page: it reads `GET /v1/limits` only.
 */
function openLimits(): void {
  const s = session;
  if (s === undefined || s.chat === undefined || s.limitsEntry !== undefined) return;
  s.chatScroll = s.chat.view.body.scrollTop;
  s.limitsFrom = "chat";
  s.limits.view.backTo("Back to Assistant");
  mount("limits", s.limits.view.parts);
  s.limitsEntry = nav.push(() => {
    s.limitsEntry = undefined;
    afterLimits(s);
    return true;
  });
  void s.limits.refresh();
}

/** The page's own Back over the chat: its entry consumed, and the chat where it was. */
function closeLimits(): void {
  const s = session;
  if (s === undefined || s.limitsEntry === undefined) return;
  nav.done(s.limitsEntry);
  s.limitsEntry = undefined;
  afterLimits(s);
}

function afterLimits(s: Session): void {
  s.limitsFrom = "me";
  if (s.chat === undefined) return;
  mount("chat", s.chat.view.parts);
  if (s.chatScroll !== undefined) s.chat.view.body.scrollTop = s.chatScroll;
}

/**
 * A tab takes over from the page over the chat: the page closes and its history entry — the newest — is RETURNED for the
 * tab to take over (Nav.swap, as `leaveAdd`); the chat under it closes without a Back of its own (an older entry).
 */
function leaveLimits(): number | undefined {
  const s = session;
  if (s === undefined || s.limitsEntry === undefined) return undefined;
  const id = s.limitsEntry;
  s.limitsEntry = undefined;
  s.limitsFrom = "me";
  if (s.chatEntry !== undefined) {
    nav.done(s.chatEntry);
    s.chatEntry = undefined;
  }
  return id;
}

/**
 * DCTA-199 ruling (Claude, 13 Oct): every card a passkey signs speaks "Check and sign." — 17 Payees' add and the identity
 * check's declaration too. Through the chat's Read aloud once this session has a chat (its choice, its last turn); before
 * that, a reader on the same choice that has heard no turn yet (Always reads; When I speak waits for a spoken turn).
 */
let cardReader: ReadAloud | undefined;
function readCard(bundleId: string): void {
  const s = session;
  if (s === undefined) return;
  if (s.chat !== undefined) {
    s.chat.readCard(bundleId);
    return;
  }
  cardReader ??= new ReadAloud({ source: speech, out: speaker, mode: () => session?.readAloud.mode ?? "when_i_speak" });
  cardReader.speak({ part: "readback", bundle_id: bundleId }, { now: true });
}

// ------------------------------------------------------------------ the identity check (DCTA-176, F45–F48)
/**
 * The refresh, one more layer with its own history entry — over the chat ("Update my details"), Home (the due banner's
 * "Update now") or Me ("Your identity check"). It rises from the bottom as a stacked screen: the composer and the mark are
 * not on screen while it is up (04-kyc §0.2 Layering). The bank is asked to start first; a bank with no refresh (not demo
 * mode) answers false, and the caller opens Talk to us instead. Back (its own, or the system's) closes it — never while a
 * passkey sheet is up — and nothing is saved; Done brings the bank's answer back where it rose from.
 */
let kycOpening = false;
async function openKyc(from: "chat" | "home" | "me" | "invest"): Promise<boolean> {
  const s = session;
  if (s === undefined || s.kycScreen !== undefined || kycOpening) return true;
  kycOpening = true;
  try {
    const screen = new KycScreen({
      api,
      host,
      customerId: s.persona.customerId,
      status: s.kyc?.status ?? "unknown",
      reduced: () => reducedMotion(root),
      now: () => bank.iso(),
      onClose: (kyc) => closeKyc(kyc),
      onSignedOut: signedOut,
      onCard: readCard,
    });
    if ((await screen.flow.open()) === "support") return false;
    if (session !== s) return true;
    if (from === "chat") s.chatScroll = s.chat?.view.body.scrollTop;
    s.kycScreen = screen;
    s.kycFrom = from;
    mount("kyc", screen.parts);
    // The screen's title is focused as it arrives (04-kyc §0.2) — only now is it in the page.
    screen.flow.view.focus();
    host.dataset["enter"] = "rise";
    window.setTimeout(() => {
      if (host.dataset["enter"] === "rise") delete host.dataset["enter"];
    }, 400);
    s.kycEntry = nav.push(() => {
      // A passkey sheet is up: the system's Back changes nothing (the ceremony is never interrupted).
      if (s.kycScreen?.busy === true) return false;
      s.kycEntry = undefined;
      s.kycScreen?.close();
      afterKyc(s, undefined);
      return true;
    });
    return true;
  } finally {
    kycOpening = false;
  }
}

/** The flow ended itself (Done, Not now, Close): its entry consumed, and back where it rose from. */
function closeKyc(kyc: KycConfirmed | undefined): void {
  const s = session;
  if (s === undefined || s.kycScreen === undefined) return;
  if (s.kycEntry !== undefined) nav.done(s.kycEntry);
  s.kycEntry = undefined;
  afterKyc(s, kyc);
}

function afterKyc(s: Session, kyc: KycConfirmed | undefined): void {
  if (kyc !== undefined) s.kycProof = s.kycScreen?.flow.proof(s.persona.customerId) ?? s.kycProof;
  s.kycScreen = undefined;
  // Home's banner and Me's row follow what the bank holds now — after Done, and after a flow that found the details
  // already up to date elsewhere (409, two tabs): the confirm's answer at once, then the session's own read.
  if (kyc !== undefined) s.kyc = { status: kyc.status, money_movement_allowed: kyc.status === "verified" };
  void api
    .session()
    .then((now) => {
      if (session !== s) return;
      s.kyc = now.kyc;
      if (host.dataset["screen"] === "home") {
        homeDue(s);
        if (s.last !== undefined) s.home.show(s.last);
      }
    })
    .catch(() => undefined);
  const from = s.kycFrom;
  if (from === "chat" && s.chat !== undefined) {
    mount("chat", s.chat.view.parts);
    if (s.chatScroll !== undefined) s.chat.view.body.scrollTop = s.chatScroll;
    // F47: row 24d and "Ask again", in the conversation the refusal was in; focus back in the conversation.
    if (kyc !== undefined) s.chat.kycDone(kyc, s.kycProof?.bundleId);
    s.chat.returnFocus();
    return;
  }
  if (from === "me") showMe();
  else if (from === "invest") showInvest();
  else showHome();
}

// ------------------------------------------------------------------ chat
/** `welcomeEntry`: DCTA-216 — the walkthrough's history entry, handed over when its lit mark opened the chat. */
function openChat(entry: "text" | "voice", welcomeEntry?: number): void {
  const s = session;
  if (s === undefined) return;
  // The mark from 17 at `add` (DCTA-120): over the chat it came from, it is simply Back; over the list, the
  // layer goes and the chat takes over its entry.
  if (s.limitsEntry !== undefined && s.chat !== undefined) {
    closeLimits();
    s.chat.open(entry);
    return;
  }
  let addHanded: number | undefined;
  if (s.addEntry !== undefined) {
    if (s.addFrom === "chat" && s.chat !== undefined) {
      closeAdd();
      s.chat.open(entry);
      return;
    }
    addHanded = leaveAdd();
  }
  const chat = chatFor(s);
  mount("chat", chat.view.parts);
  if (s.chatEntry === undefined) {
    const pop = chatPop(s);
    // From a screen pushed under Me (or Cards over it), the chat takes over the top layer's entry
    // (no done-then-push race, Nav.swap); a layer under it closes without a Back of its own.
    const cardsHanded = closeCards(true);
    const pushHanded = closePushed(true);
    if (cardsHanded !== undefined && pushHanded !== undefined) nav.done(pushHanded);
    let handed = cardsHanded ?? pushHanded;
    // DCTA-209: "Change amount or day" from a schedule over Activity — the chat takes over the Scheduled layer's entry.
    const schedHanded = closeScheduled(true, false);
    if (schedHanded !== undefined) {
      if (handed !== undefined) nav.done(handed);
      handed = schedHanded;
    }
    if (addHanded !== undefined) {
      if (handed !== undefined) nav.done(handed);
      handed = addHanded;
    }
    // DCTA-216: the walkthrough's entry is the newest — the chat takes it; any older one handed here goes.
    if (welcomeEntry !== undefined) {
      if (handed !== undefined) nav.done(handed);
      handed = welcomeEntry;
    }
    s.chatEntry = handed === undefined ? nav.push(pop) : nav.swap(handed, pop);
  } else if (welcomeEntry !== undefined) nav.done(welcomeEntry);
  // The chat rises from the mark (§C.7: --t-rise, ease-glide; a crossfade under reduced motion).
  host.dataset["enter"] = "rise";
  window.setTimeout(() => {
    if (host.dataset["enter"] === "rise") delete host.dataset["enter"];
  }, 400);
  chat.open(entry);
}

/** A screen's action asked in words (Cards' Pay the bill, Security's Unlock my money): the chat, the words sent as typed. */
function askInChat(words: string): void {
  openChat("text");
  void session?.chat?.say(words);
}

/** The chat's Back: where it was opened from — never while a signature is in flight. */
function chatPop(s: Session): () => boolean {
  return () => {
    if (session?.chat?.signing === true) return false;
    s.chatEntry = undefined;
    show(s.from);
    return true;
  };
}

/**
 * QA-04: back in the chat after a reload — the conversation as it was (drawn into the chat before it is in the page), on
 * the entry the browser is on (Back = Home). Nothing rises and nothing is focused: the page is where it was.
 */
function resumeChat(s: Session): void {
  const chat = chatFor(s);
  s.from = "home";
  mount("chat", chat.view.parts);
  s.chatEntry = nav.adopt(chatPop(s));
}

/** The session's chat — made once; QA-04: with the conversation a reload kept drawn back into it first. */
function chatFor(s: Session): Chat {
  if (s.chat !== undefined) return s.chat;
  const chat = new Chat({
    api,
    persona: s.persona,
    name: s.name,
    host,
    root,
    recognizer,
    readAloud: { source: speech, out: speaker, mode: () => s.readAloud.mode },
    amend,
    onBack: () => leaveChat(),
    onSignedOut: signedOut,
    judgeOn: () => session?.judge === true,
    demoFault: s.demoFault,
    audio: clip,
    bankNow: () => bank.now(),
    onAddPayee: () => openAdd("chat"),
    onLimits: () => openLimits(),
    onActivity: () => go("activity"),
    // DCTA-209 (F56, F61): a signed schedule's View, a read card's row — the schedule, over the chat.
    onSchedule: (mandateId) => openScheduled("chat", mandateId),
    onKycRefresh: () => openKyc("chat"),
    onMoneyMoved: () => void afterPayment(),
    onBankChanged: bankChanged,
    onReceipt: (bundleId) => {
      const now = session;
      if (now === undefined) return;
      // The chat is left for Activity: its Back entry becomes Activity's (Back = Home, P2-3).
      const handed = now.chatEntry;
      now.chatEntry = undefined;
      tabLayer(now, "activity", handed);
      now.from = "activity";
      mount("activity", now.activity.view.parts);
      void now.activity.reveal(bundleId);
    },
  });
  // QA-04: the conversation a reload kept, drawn back before the chat is in the page (the log reads none of it out).
  if (s.kept !== undefined) chat.restore(s.kept);
  s.kept = undefined;
  s.chat = chat;
  return chat;
}

/** The chat's own back: where it was opened from, its history entry consumed. */
function leaveChat(): void {
  const s = session;
  if (s === undefined) return;
  if (s.chatEntry !== undefined) nav.done(s.chatEntry);
  s.chatEntry = undefined;
  show(s.from);
}

function signedOut(): void {
  resumeStore.forget();
  welcomeOff();
  nav.reset();
  session?.scheduled?.dispose();
  session?.kycScreen?.close();
  session?.chat?.dispose();
  session = undefined;
  door("signed-out");
}

/** The customer chose Sign out on Me: back to the door, with no banner to explain. */
function leave(): void {
  resumeStore.forget();
  welcomeOff();
  nav.reset();
  session?.scheduled?.dispose();
  session?.kycScreen?.close();
  session?.chat?.dispose();
  session = undefined;
  door();
}

// ------------------------------------------------------------------ a reload (QA-04)
/** How long a reload waits for the bank to confirm the session before it shows the door instead. */
const RESUME_WAIT_MS = 8000;

function within<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("the bank did not answer in time")), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

/**
 * QA-04 (Mario, 13 Oct; the coordinator's expectation, 14 Oct): a reload keeps the session. The bank says whether it still
 * holds it (GET /v1/session, with this tab's token given back) before anything signed-in is drawn — and teaches the bank's
 * clock (/healthz), which a card read back after the reload expires by. Still held: straight back in, where it was. Not
 * held (signed out elsewhere, expired, another customer): the door, and nothing of it is kept. Not answered (offline, the
 * bank down): the door says the bank can't be reached, and a reload once it is back comes straight in.
 */
async function resume(r: Resume): Promise<void> {
  const persona = PERSONAS.find((p) => p.customerId === r.customerId);
  if (persona === undefined) {
    resumeStore.forget();
    door();
    return;
  }
  api.restoreCsrf(r.csrf);
  let held: SessionView;
  let health: Health | undefined;
  try {
    [held, health] = await within(Promise.all([api.session(), api.health().catch(() => undefined)]), RESUME_WAIT_MS);
  } catch (error) {
    // The bank no longer holds the session (401), or this customer (403, 404): nothing of it is kept.
    if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
      resumeStore.forget();
      door();
    } else door("error");
    return;
  }
  if (held.customer_id !== r.customerId) {
    resumeStore.forget();
    door();
    return;
  }
  // A bank that hands the token back with the session (docs/specs/fe-reload-resume-qa04.md, ask B-QA04-1) is the one used.
  api.restoreCsrf(held.csrf_token);
  const demo = health?.demo_mode ?? r.demo;
  signedIn(persona, held.display_name, held.accessibility_profile === "large_type", held.preferences, held.kyc, demo, {
    place: r.place,
    judge: r.judge,
    chat: resumeStore.chat(r.customerId),
  });
}

/** `pagehide` came: the page's last word is written — what a reload cancels after it is not part of what the page showed. */
let gone = false;

/**
 * Where the customer is, judge mode and what the chat shows — kept as the page goes away (`pagehide`), and whenever it is
 * hidden (a phone may never bring it back).
 */
function keepSession(): void {
  const s = session;
  if (s === undefined || gone) return;
  resumeStore.note(s.place, s.judge);
  if (s.chat !== undefined) resumeStore.keepChat(s.chat.kept(s.persona.customerId));
}
window.addEventListener("pagehide", () => {
  keepSession();
  gone = true;
});
window.addEventListener("pageshow", () => {
  gone = false;
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") keepSession();
});

const signedInHere = resumeStore.read();
if (signedInHere === undefined) door();
else void resume(signedInHere);

// DCTA-87: no service worker. A phone installed on an older build may still be controlled
// by the caching worker builds up to 13 shipped; retire it (and its caches). `/sw.js` is the
// kill switch for phones that never run this line first.
void retireFromPage(
  navigator,
  "caches" in window ? window.caches : undefined,
  reloadOncePerSession(sessionStorageOrUndefined(), () => location.reload()),
);
